import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress, Alert, Button, Card, CardContent } from '@mui/material';
import { authService } from '../services/auth';
import { errorMessage } from '../services/api';
import SiteFooter from '../components/SiteFooter';

const VerifyMagicLink: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Signing you in...');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // React 18 StrictMode double-invokes effects; the token is single-use.
    ran.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('This link is missing its sign-in code.');
      return;
    }

    authService
      .verifyMagicLink(token)
      .then(({ redirect }) => {
        setStatus('success');
        setMessage('You\'re signed in!');
        const target = redirect && redirect.startsWith('/') ? redirect : '/dashboard';
        setTimeout(() => navigate(target, { replace: true }), 800);
      })
      .catch((error) => {
        // Already signed in on this device? The link was probably opened twice.
        if (authService.isAuthenticated()) {
          navigate('/dashboard', { replace: true });
          return;
        }
        setStatus('error');
        setMessage(errorMessage(error, 'This link is invalid or has expired.'));
      });
  }, [searchParams, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {status === 'verifying' && (
              <>
                <CircularProgress size={36} color="secondary" sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontFamily: 'inherit' }}>{message}</Typography>
              </>
            )}
            {status === 'success' && <Alert severity="success">{message}</Alert>}
            {status === 'error' && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Sign-in links work once. Request a new one and open it on this device.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/')}>Get a new link</Button>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
      <SiteFooter />
    </Box>
  );
};

export default VerifyMagicLink;
